import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

interface GalleryImage {
  id: string
  url: string
  alt: string
  title: string
}

interface GalleryData {
  title: string
  subtitle: string
  images: GalleryImage[]
}

interface GalleryProps {
  data: GalleryData
}

const CARD_BASE =
  'relative flex-shrink-0 overflow-hidden rounded-2xl shadow-lg cursor-pointer group'

const CARD_SIZE =
  'w-[180px] sm:w-[220px] md:w-[250px] lg:w-[270px] aspect-square'

const Gallery: React.FC<GalleryProps> = ({ data }) => {
  const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null)

  const mid = Math.ceil(data.images.length / 2)
  const row1 = [...data.images.slice(0, mid)]
  const row2 = [...data.images.slice(mid)]

  const loop = (arr: GalleryImage[]) => [...arr, ...arr, ...arr]

  // Close on ESC
  useEffect(() => {
    const handler = (e: KeyboardEvent) =>
      e.key === 'Escape' && setSelectedImage(null)
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [])

  return (
    <section
      id="gallery"
      className="py-16 bg-white dark:bg-custom-dark overflow-hidden"
    >
      {/* Header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 mb-14 text-center">
        <h2 className="font-display font-bold text-gray-900 dark:text-white text-[clamp(2rem,4.5vw,3rem)] mb-4">
          {data.title}
        </h2>
        <p className="mx-auto max-w-2xl text-gray-600 dark:text-gray-300 text-[clamp(1rem,1.6vw,1.2rem)]">
          {data.subtitle}
        </p>
      </div>

      {/* Rows */}
      <div className="space-y-8">
        {[loop(row1), loop(row2)].map((row, rowIndex) => (
          <div key={rowIndex} className="relative">
            <div
              className={`flex gap-6 ${
                rowIndex === 0
                  ? 'animate-marquee-left'
                  : 'animate-marquee-right'
              }`}
            >
              {row.map((image, i) => (
                <motion.div
                  key={`${rowIndex}-${i}`}
                  whileHover={{ y: -8 }}
                  transition={{ type: 'spring', stiffness: 120, damping: 16 }}
                  onClick={() => setSelectedImage(image)}
                  className={`${CARD_BASE} ${CARD_SIZE}`}
                >
                  <img
                    src={image.url}
                    alt={image.alt}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </motion.div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedImage(null)}
          >
            <motion.div
              onClick={(e) => e.stopPropagation()}
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 280, damping: 26 }}
              className="relative max-w-2xl w-full flex flex-col items-center"
            >
              <div className="relative inline-block">
                <img
                  src={selectedImage.url}
                  alt={selectedImage.alt}
                  className="max-h-[75vh] object-contain rounded-xl shadow-2xl"
                />
                
                <button
                  className="absolute top-4 right-4 bg-black/50 hover:bg-black/70 rounded-full p-2 transition-colors z-10"
                  onClick={() => setSelectedImage(null)}
                  aria-label="Close"
                >
                  <span className="material-symbols-outlined text-3xl text-white">
                    close
                  </span>
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  )
}

export default Gallery
